import { CalendarDays, Pencil, Plus, Swords, TrendingUp } from "lucide-react";
import {
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  PillGroup,
} from "@/components/market-intel/components";
import { formatShortDate } from "@/components/market-intel/dates";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { hostOf, riseStyle } from "@/components/market-intel/util";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import {
  filterTrends,
  isRelevance,
  RELEVANCE_FILTERS,
  relevanceLabelKey,
  sortTrends,
  type RelevanceFilter,
} from "./intel";
import { PlatformBadge } from "./PlatformIcon";
import {
  isPlatformFilter,
  PLATFORM_FILTERS,
  platformFilterLabelKey,
  type PlatformFilter,
} from "./platforms";
import { competitorPath } from "./routes";
import { TrendForm } from "./TrendForm";
import type { MarketTrend } from "./types";
import {
  AddButton,
  ConfirmDeleteButton,
  IconAction,
  RelevanceBadge,
  SectionCard,
} from "./ui";

const useCompetitorNames = () => {
  const { data } = useGetList<MarketCompetitor>("market_competitors", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });
  return useMemo(() => data ?? [], [data]);
};

/** URL state: ?red=…&relevancia=… */
const useTrendFilters = () => {
  const [params, setParams] = useSearchParams();
  const red = params.get("red");
  const relevancia = params.get("relevancia");
  const platform: PlatformFilter = isPlatformFilter(red) ? red : "all";
  const relevance: RelevanceFilter = isRelevance(relevancia)
    ? relevancia
    : "all";
  const setFilters = (next: {
    platform: PlatformFilter;
    relevance: RelevanceFilter;
  }) => {
    const query: Record<string, string> = {};
    if (next.platform !== "all") query.red = next.platform;
    if (next.relevance !== "all") query.relevancia = next.relevance;
    setParams(query, { replace: true });
  };
  return { platform, relevance, setFilters };
};

/** /competencia/tendencias: trends across networks, newest first. */
export const TrendsView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { platform, relevance, setFilters } = useTrendFilters();
  const [editing, setEditing] = useState<MarketTrend | "new" | null>(null);
  const { data, isPending, error } = useGetList<MarketTrend>("market_trends", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "detected_at", order: "DESC" },
  });
  const competitors = useCompetitorNames();
  const visible = filterTrends(data ?? [], platform, relevance);
  const filtered = platform !== "all" || relevance !== "all";
  const addLabel = translate("competition.trends.add");

  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("competition.trends.heading")}</h2>
      <div className="space-y-2">
        <div className="mi-pills-row md:items-start md:justify-between">
          <PillGroup<PlatformFilter>
            label={translate("competition.platform_filter.label")}
            value={platform}
            options={PLATFORM_FILTERS.map((value) => ({
              value,
              label: translate(platformFilterLabelKey(value)),
            }))}
            onChange={(next) => setFilters({ platform: next, relevance })}
          />
          <Button
            type="button"
            onClick={() => setEditing("new")}
            aria-label={isMobile ? addLabel : undefined}
            title={isMobile ? addLabel : undefined}
            className={cn(
              "btn-save shrink-0 rounded-full",
              isMobile ? "size-9 p-0" : "px-4",
            )}
          >
            <Plus aria-hidden className={isMobile ? "size-5" : "size-4"} />
            {isMobile ? null : addLabel}
          </Button>
        </div>
        <PillGroup<RelevanceFilter>
          label={translate("competition.trends.relevance.label")}
          value={relevance}
          options={RELEVANCE_FILTERS.map((value) => ({
            value,
            label: translate(
              value === "all"
                ? "competition.trends.relevance.all"
                : relevanceLabelKey(value),
            ),
          }))}
          onChange={(next) => setFilters({ platform, relevance: next })}
        />
      </div>
      {isPending ? (
        <LoadingCards />
      ) : error ? (
        <EmptyState icon={TrendingUp} title={translate("market.error")} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title={translate(
            data?.length
              ? "competition.trends.empty_filtered"
              : "competition.trends.empty_all",
          )}
          hint={
            data?.length
              ? undefined
              : translate("competition.trends.empty_hint")
          }
          action={
            filtered && data?.length ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() =>
                  setFilters({ platform: "all", relevance: "all" })
                }
              >
                {translate("competition.trends.clear_filters")}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
          {visible.map((trend, index) => (
            <TrendCard
              key={trend.id}
              trend={trend}
              competitor={competitors.find(
                (competitor) =>
                  String(competitor.id) === String(trend.competitor_id),
              )}
              onEdit={() => setEditing(trend)}
              index={index}
            />
          ))}
        </div>
      )}
      <TrendForm
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
        trend={editing === "new" ? null : editing}
        competitors={competitors}
      />
    </section>
  );
};

export const TrendCard = ({
  trend,
  competitor,
  onEdit,
  index,
  compact,
}: {
  trend: MarketTrend;
  competitor?: MarketCompetitor;
  onEdit: () => void;
  index: number;
  /** Inside the competitor's page: a flat tile, no competitor link. */
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove, { isPending }] = useDelete<MarketTrend>();
  const titleId = `trend-${trend.id}${compact ? "-c" : ""}`;
  return (
    <article
      aria-labelledby={titleId}
      data-platform={trend.platform}
      className={cn(
        "flex min-w-0 flex-col gap-2",
        compact
          ? "rounded-2xl bg-muted/50 p-3 dark:bg-white/5"
          : "mi-card mi-rise p-4 md:p-5",
      )}
      style={compact ? undefined : riseStyle(index)}
    >
      <div className="flex min-w-0 items-center gap-2">
        <PlatformBadge platform={trend.platform} size="sm" showLabel />
        <RelevanceBadge relevance={trend.relevance} />
        <div className="ml-auto flex shrink-0 -mr-1.5">
          <IconAction
            label={`${translate("competition.trends.edit")}: ${trend.title}`}
            icon={Pencil}
            onClick={onEdit}
          />
          <ConfirmDeleteButton
            label={`${translate("competition.form.delete")}: ${trend.title}`}
            disabled={isPending}
            onConfirm={() =>
              remove(
                "market_trends",
                { id: trend.id, previousData: trend },
                {
                  mutationMode: "optimistic",
                  onSuccess: () =>
                    notify("competition.form.deleted", { type: "info" }),
                  onError: () =>
                    notify("competition.form.error", { type: "error" }),
                },
              )
            }
          />
        </div>
      </div>
      <h3
        id={titleId}
        className={cn(
          "leading-snug font-semibold wrap-anywhere",
          compact ? "text-[15px]" : "text-[17px]",
        )}
      >
        {trend.title}
      </h3>
      {trend.description ? (
        <MiniMarkdown
          source={trend.description}
          className={cn("text-[15px]", compact && "line-clamp-3 text-sm")}
        />
      ) : null}
      <div className="mt-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 pt-1 text-sm">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <CalendarDays aria-hidden className="size-3.5" />
          {translate("competition.trends.detected", {
            date: formatShortDate(trend.detected_at, locale),
          })}
        </span>
        {trend.url ? (
          <ExternalAnchor href={trend.url}>{hostOf(trend.url)}</ExternalAnchor>
        ) : null}
        {competitor && !compact ? (
          <Link
            to={competitorPath(competitor.id)}
            className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/15 dark:bg-primary/15"
          >
            <Swords aria-hidden className="size-3.5 shrink-0" />
            <span className="truncate">
              {translate("competition.trends.linked", {
                name: competitor.name,
              })}
            </span>
          </Link>
        ) : null}
      </div>
    </article>
  );
};

/** The competitor page's "Tendencias relacionadas" card. */
export const RelatedTrends = ({
  id,
  competitorId,
}: {
  id: string;
  competitorId: Identifier;
}) => {
  const translate = useTranslate();
  const [editing, setEditing] = useState<MarketTrend | "new" | null>(null);
  const { data } = useGetList<MarketTrend>("market_trends", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "detected_at", order: "DESC" },
    filter: { competitor_id: competitorId },
  });
  const competitors = useCompetitorNames();
  const trends = sortTrends(data ?? []);
  return (
    <SectionCard
      id={id}
      icon={TrendingUp}
      title={translate("competition.detail.related_trends")}
      count={trends.length || undefined}
      style={riseStyle(6)}
      action={
        <AddButton
          label={translate("competition.trends.add")}
          onClick={() => setEditing("new")}
        />
      }
    >
      {trends.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("competition.detail.no_related_trends")}
        </p>
      ) : (
        <div className="space-y-2.5">
          {trends.map((trend, index) => (
            <TrendCard
              key={trend.id}
              trend={trend}
              onEdit={() => setEditing(trend)}
              index={index}
              compact
            />
          ))}
        </div>
      )}
      <TrendForm
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
        trend={editing === "new" ? null : editing}
        competitors={competitors}
        defaultCompetitorId={competitorId}
      />
    </SectionCard>
  );
};
